import { z } from 'zod';
/**
 * Team AI: a playground's assistant on a key the team holds (docs/playground-team-ai-plan.md).
 *
 * The contract between the three parts that implement it: the Worker, which holds the key and
 * mints what a sandbox is given (`apps/api/src/routes/team-ai.ts`); agentd, which points Codex at
 * the provider with it (`playground/agentd/src/team-ai.ts`); and the dashboard, where an admin
 * gives the key and a developer chooses the source (`apps/web`).
 *
 * Codex runs unchanged on a custom model provider. The pinned CLI only speaks the Responses API
 * (`wire_api = "chat"` is refused by 0.159.2), so a provider is listed here only if it serves
 * `/responses` under `baseUrl` (plan §1).
 */
export const TeamAiProvider = z.enum(['openrouter', 'cheaperinference']);
export const TEAM_AI_PROVIDERS = {
    openrouter: {
        label: 'OpenRouter',
        baseUrl: 'https://openrouter.ai/api/v1',
        keyKind: 'management',
        sessionKeys: true,
        enforcesCap: true,
    },
    cheaperinference: {
        label: 'Cheaper Inference',
        baseUrl: 'https://api.cheaperinference.com/v1',
        keyKind: 'inference',
        sessionKeys: false,
        enforcesCap: false,
    },
};
/** Which credential a playground's assistant runs on: the developer's ChatGPT, or the team's AI. */
export const CredentialSource = z.enum(['chatgpt', 'team']);
/* ------------------------------------------------------------------------------------- money */
/**
 * Money is integer micro-dollars wherever it is stored or sent. OpenRouter reports usage as a
 * float in dollars, and a sum of floats is not a budget.
 */
export const MICRO_PER_USD = 1_000_000;
export function usdToMicro(usd) {
    return Math.round(usd * MICRO_PER_USD);
}
/** `$12.34`. Two places, because a budget is read in cents. */
export function formatUsd(micro) {
    return `$${(micro / MICRO_PER_USD).toFixed(2)}`;
}
/** How much of the month's budget one reservation takes (plan §4). */
export const TEAM_AI_CHUNK_MICRO = 2 * MICRO_PER_USD;
/** A session key with less than this left is topped up after a turn (plan §4). */
export const TEAM_AI_TOPUP_BELOW_MICRO = TEAM_AI_CHUNK_MICRO / 2;
/**
 * How long a session key lives. Longer than a session normally runs; a session kept alive past it
 * asks again and is given a new one (the Worker releases the old one first), so this is a backstop
 * for a key nothing released, not a session limit.
 */
export const TEAM_AI_SESSION_KEY_TTL_MS = 6 * 60 * 60 * 1000;
/** The largest monthly cap the Team page accepts. */
export const MAX_TEAM_AI_CAP_USD = 10_000;
/** The month a budget row belongs to: the UTC calendar month, `YYYY-MM`. */
export function budgetMonth(at) {
    return new Date(at).toISOString().slice(0, 7);
}
/* ------------------------------------------------------------------------------------ models */
/**
 * The efforts a curated model may list. A subset of agentd's `EFFORTS` (`assistant.ts`): the ones a
 * Responses provider forwards as `reasoning.effort`.
 */
export const TeamAiEffort = z.enum(['minimal', 'low', 'medium', 'high', 'xhigh']);
/**
 * The list as it shipped in code, and what migration 0075 seeded `team_ai_models` with.
 *
 * **Not the list.** Since that migration the list is the table: an operator curates it in the admin
 * console (docs/admin-plan.md §D5 row 13), and the public API reads it with `teamAiCatalogue`
 * (packages/worker-core). Changing this array changes nothing in a deployed database. It stays as
 * the fixture the tests run on, and apps/api/test/team-ai.test.ts holds it equal to the migration's
 * rows, so it cannot drift into looking like something it is not.
 *
 * Written on 2026-10-13 from OpenRouter's public catalogue and Cheaper Inference's `/markets` page,
 * before stage 0's evaluation; the Cheaper Inference ids were checked against a team key's
 * `GET /v1/models` on 2026-10-14.
 */
export const TEAM_AI_SEED_MODELS = [
    {
        id: 'gpt-6.1-sol',
        label: 'GPT-6.1 Sol',
        ids: { openrouter: 'openai/gpt-6.1-sol', cheaperinference: 'gpt-6.1-sol' },
        contextWindow: 1_050_000,
        autoCompactTokenLimit: 240_000,
        efforts: ['low', 'medium', 'high', 'xhigh'],
        defaultEffort: 'medium',
        verifiedAt: null,
    },
    {
        id: 'gpt-5.6-sol',
        label: 'GPT-5.6 Sol',
        ids: { openrouter: 'openai/gpt-5.6-sol', cheaperinference: 'gpt-5.6-sol' },
        contextWindow: 1_050_000,
        autoCompactTokenLimit: 240_000,
        efforts: ['low', 'medium', 'high', 'xhigh'],
        defaultEffort: 'medium',
        verifiedAt: null,
    },
    {
        id: 'gpt-6-luna',
        label: 'GPT-6 Luna',
        ids: { openrouter: 'openai/gpt-6-luna', cheaperinference: 'gpt-6-luna' },
        contextWindow: 1_050_000,
        autoCompactTokenLimit: 240_000,
        efforts: ['low', 'medium', 'high'],
        defaultEffort: 'medium',
        verifiedAt: null,
    },
    {
        id: 'deepseek-v4.1-flash',
        label: 'DeepSeek V4.1 Flash',
        ids: { openrouter: 'deepseek/deepseek-v4.1-flash', cheaperinference: 'deepseek-v4.1-flash' },
        contextWindow: 1_048_576,
        autoCompactTokenLimit: 200_000,
        efforts: ['low', 'medium', 'high'],
        defaultEffort: 'medium',
        verifiedAt: null,
    },
    {
        id: 'glm-5.3',
        label: 'GLM-5.3',
        ids: { openrouter: 'z-ai/glm-5.3', cheaperinference: 'glm-5.3' },
        contextWindow: 1_048_576,
        autoCompactTokenLimit: 200_000,
        efforts: ['low', 'medium', 'high'],
        defaultEffort: 'medium',
        verifiedAt: null,
    },
    {
        id: 'kimi-k3',
        label: 'Kimi K3',
        ids: { openrouter: 'moonshotai/kimi-k3', cheaperinference: 'kimi-k3' },
        contextWindow: 1_048_576,
        autoCompactTokenLimit: 200_000,
        efforts: ['low', 'medium', 'high'],
        defaultEffort: 'medium',
        verifiedAt: null,
    },
    {
        id: 'minimax-m3',
        label: 'MiniMax M3',
        ids: { openrouter: 'minimax/minimax-m3', cheaperinference: 'minimax-m3' },
        contextWindow: 1_048_576,
        autoCompactTokenLimit: 200_000,
        efforts: [],
        defaultEffort: null,
        verifiedAt: null,
    },
];
/** The models of `catalogue` a team's provider serves, minus the ones its admin switched off, in list order. */
export function teamAiModelsFor(catalogue, provider, off) {
    return catalogue.filter((model) => model.ids[provider] !== undefined && !off.includes(model.id));
}
/** One model as a sandbox is handed it: BoardFlight's id, and the provider's id already resolved. */
export const PlaygroundTeamAiModel = z.object({
    id: z.string(),
    label: z.string(),
    /** What goes into Codex's `model`. */
    model: z.string(),
    contextWindow: z.number().int().positive(),
    autoCompactTokenLimit: z.number().int().positive(),
    efforts: z.array(TeamAiEffort),
    defaultEffort: TeamAiEffort.nullable(),
});
export function playgroundTeamAiModels(catalogue, provider, off) {
    return teamAiModelsFor(catalogue, provider, off).map((model) => ({
        id: model.id,
        label: model.label,
        model: model.ids[provider],
        contextWindow: model.contextWindow,
        autoCompactTokenLimit: model.autoCompactTokenLimit,
        efforts: [...model.efforts],
        defaultEffort: model.defaultEffort,
    }));
}
/* ------------------------------------------------------------------------- the Team page */
/** As long as a team MCP key may be, and refused on the same characters, for the same reason. */
export const MAX_TEAM_AI_KEY_CHARS = 4096;
const TeamAiKey = z
    .string()
    .trim()
    .min(1, 'Paste the key.')
    .max(MAX_TEAM_AI_KEY_CHARS)
    // eslint-disable-next-line no-control-regex
    .refine((key) => !/[\x00-\x1f\x7f]/.test(key), 'A key cannot contain line breaks or control characters.');
const MonthlyCapUsd = z
    .number()
    .positive('Set a monthly limit above $0.')
    .max(MAX_TEAM_AI_CAP_USD, `The monthly limit can be at most $${MAX_TEAM_AI_CAP_USD.toLocaleString('en-US')}.`);
const ModelsOff = z.array(z.string().max(64)).max(64);
/**
 * `PUT /v1/teams/:teamId/ai`: an admin giving the team its AI provider key. A team holds one;
 * putting another replaces it.
 *
 * OpenRouter needs a monthly limit, because BoardFlight is what enforces it. Cheaper Inference
 * takes none, because BoardFlight cannot enforce one there; the Team page asks the admin to set
 * it on the key in Cheaper Inference instead.
 */
export const SetTeamAiRequest = z.discriminatedUnion('provider', [
    z.object({ provider: z.literal('openrouter'), key: TeamAiKey, monthlyCapUsd: MonthlyCapUsd, modelsOff: ModelsOff.optional() }),
    z.object({ provider: z.literal('cheaperinference'), key: TeamAiKey, modelsOff: ModelsOff.optional() }),
]);
/** `PATCH /v1/teams/:teamId/ai`: the limit or the models, without touching the key. */
export const UpdateTeamAiRequest = z
    .object({ monthlyCapUsd: MonthlyCapUsd.optional(), modelsOff: ModelsOff.optional() })
    .refine((body) => body.monthlyCapUsd !== undefined || body.modelsOff !== undefined, 'Nothing to change.');
/**
 * The team's AI as anyone on the team may see it: the provider, the key's last four characters,
 * the limit and this month's spending. Never the key itself, not even to the admin who set it.
 *
 * `setBy` is decided by `nameFor` (`packages/worker-core/src/identity.ts`), as a team MCP key's is.
 */
export const TeamAi = z.object({
    provider: TeamAiProvider,
    keyHint: z.string(),
    /** `null` for a provider whose limit BoardFlight does not enforce. */
    monthlyCapMicro: z.number().int().nullable(),
    modelsOff: z.array(z.string()),
    /** The month `spentMicro` and `reservedMicro` are for: `YYYY-MM`, UTC. */
    month: z.string(),
    /** `null` where BoardFlight cannot know: a provider it does not mint keys at. */
    spentMicro: z.number().int().nullable(),
    /** Set aside for the session keys alive right now. */
    reservedMicro: z.number().int().nullable(),
    setBy: z.string().nullable(),
    setAt: z.number().int(),
});
/**
 * One model of the list as the Team page draws it: what to call it and which providers serve it.
 * The provider's ids are not a secret; they are what each provider's own catalogue calls the model.
 */
export const TeamAiModelChoice = z.object({
    id: z.string(),
    label: z.string(),
    ids: z.object({ openrouter: z.string().optional(), cheaperinference: z.string().optional() }),
});
/**
 * `GET /v1/teams/:teamId/ai`, and the answer to every write there.
 *
 * `models` is the whole list in its order, for every provider, so a page replacing an OpenRouter key
 * with a Cheaper Inference one knows what the new one serves. It is empty for a team that neither
 * holds a key nor has `playground.teamai`: a list of models a team cannot use says that somebody else
 * can.
 */
export const TeamAiResponse = z.object({ ai: TeamAi.nullable(), models: z.array(TeamAiModelChoice) });
/* ---------------------------------------------------------------------------- the sandbox */
/**
 * `GET /v1/playgrounds/:appId/ai?peek=1`, asked with a sandbox's session secret: whether the game's
 * team has an AI this sandbox may run on, and what it would run — everything of the answer below
 * except the key. Asked at boot, so the chooser can offer *Use <team>'s AI* (plan §0.2: a choice,
 * never a default). It mints nothing, reserves nothing and releases nothing, and it hands no key
 * over, Cheaper Inference's included: a sandbox nobody chose the team's AI in never holds one.
 */
export const PlaygroundTeamAiOffer = z.object({
    provider: TeamAiProvider,
    /** The game's team, for "<team>'s AI" in the chat. Everyone in the sandbox is on that team. */
    teamName: z.string(),
    baseUrl: z.string().url(),
    models: z.array(PlaygroundTeamAiModel).min(1),
});
/**
 * `GET /v1/playgrounds/:appId/ai`, asked with a sandbox's session secret once the developer chose
 * the team's AI: what its assistant runs on. For OpenRouter, `key` is a session key minted for this
 * answer and returned nowhere else; for Cheaper Inference it is the team's key itself.
 *
 * The key goes into the Codex process's environment and nowhere else: not `config.toml`, not the
 * snapshot, not the transcript, not a log.
 */
export const PlaygroundTeamAi = PlaygroundTeamAiOffer.extend({
    key: z.string().min(1),
    /** The session key's spending limit; `null` for a provider BoardFlight does not mint keys at. */
    limitMicro: z.number().int().nullable(),
    /** When the session key stops working; `null` likewise. */
    expiresAt: z.number().int().nullable(),
});
/**
 * `POST /v1/playgrounds/:appId/ai/check`, after every team-AI turn: how the session key stands,
 * after any top-up the Worker just made (plan §4). `budgetSpent` is the month's budget gone and this
 * key at its limit, so the next turn would be refused.
 */
export const PlaygroundTeamAiCheck = z.object({
    limitMicro: z.number().int(),
    usedMicro: z.number().int(),
    budgetSpent: z.boolean(),
});
/** The error codes the sandbox routes answer with, besides the shared ones. */
export const TEAM_AI_ERROR = {
    /** The game's team holds no AI key: 404. The assistant asks for ChatGPT, as it always did. */
    none: 'team_ai_none',
    /** The month's budget is gone: 409. */
    budgetSpent: 'ai_budget_spent',
    /** The provider refused the team's key (revoked, wrong kind, out of credit): 502. */
    providerRefused: 'ai_provider_refused',
    /** The provider did not answer: 503. */
    providerUnavailable: 'ai_provider_unavailable',
    /** Two asks for the same session's key at once: 409. The loser asks again in a moment. */
    keyBusy: 'ai_key_busy',
    /** The deployment cannot hold a team's AI key (no wrapping secret): 503. */
    unavailable: 'team_ai_unavailable',
};
/**
 * agentd's `POST /auth/source`: which source the developer prefers, sent by the browser on connect
 * and when they switch. The browser owns the preference (one per browser, as the ChatGPT credential
 * is); the sandbox owns what it actually runs on, and reports it in `CredentialState.source`.
 */
export const AuthSourceRequest = z.object({ source: CredentialSource });
/* ---------------------------------------------------------------------- the admin console */
/** BoardFlight's own id for a model. What a team's `models_off` names, so it never changes once used. */
export const TeamAiModelId = z
    .string()
    .regex(/^[a-z0-9][a-z0-9.-]{0,63}$/, 'An id is lower-case letters, digits, dots and dashes, at most 64 characters.');
/** A provider's id for a model, as its `/models` lists it: `openai/gpt-6.1-sol`, `glm-5.3`. */
export const TeamAiProviderModelId = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:/@+-]*$/, 'A provider’s model id is letters, digits and . _ : / @ + -.');
/**
 * `PUT /v1/admin/ai-models/:id`: one model of the list, written whole — a new one goes to the end.
 * Both providers' ids are stated, `null` for a provider that does not serve it, so an edit that
 * takes a model away from a provider says so rather than leaving it out.
 */
export const SetTeamAiModelRequest = z
    .object({
    label: z.string().trim().min(1, 'Name the model.').max(64),
    ids: z.object({
        openrouter: TeamAiProviderModelId.nullable(),
        cheaperinference: TeamAiProviderModelId.nullable(),
    }),
    contextWindow: z.number().int().min(8_000).max(10_000_000),
    autoCompactTokenLimit: z.number().int().min(8_000),
    efforts: z.array(TeamAiEffort).max(TeamAiEffort.options.length),
    defaultEffort: TeamAiEffort.nullable(),
    verified: z.boolean(),
})
    .superRefine((body, ctx) => {
    if (body.ids.openrouter === null && body.ids.cheaperinference === null) {
        ctx.addIssue({ code: 'custom', path: ['ids'], message: 'At least one provider has to serve the model.' });
    }
    if (body.autoCompactTokenLimit > body.contextWindow) {
        ctx.addIssue({ code: 'custom', path: ['autoCompactTokenLimit'], message: 'Compaction has to start inside the context window.' });
    }
    if (new Set(body.efforts).size !== body.efforts.length) {
        ctx.addIssue({ code: 'custom', path: ['efforts'], message: 'An effort is listed twice.' });
    }
    if (body.efforts.length === 0 ? body.defaultEffort !== null : body.defaultEffort === null || !body.efforts.includes(body.defaultEffort)) {
        ctx.addIssue({
            code: 'custom',
            path: ['defaultEffort'],
            message: 'The default effort has to be one of the model’s efforts, and there is none without them.',
        });
    }
});
/** `PUT /v1/admin/ai-models`: the list's new order, naming every model on it exactly once. */
export const OrderTeamAiModelsRequest = z.object({ order: z.array(TeamAiModelId).min(1).max(200) });
/**
 * `POST /v1/admin/ai-provider-models`: what a provider serves, read by the admin Worker. OpenRouter's
 * list is public and takes no key; Cheaper Inference's needs one, pasted for this one request, used
 * for it and kept nowhere (docs/admin-plan.md §D5 row 13).
 */
export const ListProviderModelsRequest = z.discriminatedUnion('provider', [
    z.object({ provider: z.literal('openrouter') }),
    z.object({ provider: z.literal('cheaperinference'), key: TeamAiKey }),
]);
//# sourceMappingURL=team-ai.js.map